import { useTranslation } from 'react-i18next';
import SCUIGenericExcelUpload from '@/atomic/molecules/SCUIGenericExcelUpload/SCUIGenericExcelUpload';
import { SCUICustomDataGrid } from '@/atomic/molecules/SCUICustomDataGrid/SCUICustomDataGrid';
import { Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, Chip, Paper } from '@mui/material';
import Swal from 'sweetalert2';
import { useState } from 'react';
import MPPaymentOnDeliveryCommissionCommand from '@/services/command/mpPaymentOnDeliveryCommission/MPPaymentOnDeliveryCommission.services';
import { MPODC } from '../mpPaymentOnDeliveryCommission.classes';

export const BulkExcelUploadTab = ({
    setIsLoading,
    isLoading,
    companyOptions,
}: {
    setIsLoading: (isLoading: boolean) => void;
    isLoading: boolean;
    companyOptions: any[];
}) => {
    const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
    const [uploadResponse, setUploadResponse] = useState<any>(null);

    const { t } = useTranslation();

    const renderHighlightedToken = (text: string, token: string) => {
        const parts = text.split(token);
        if (parts.length <= 1) return text;

        return (
            <>
                {parts.map((part, idx) => (
                    <span style={{ fontSize: '12px' }} key={idx}>
                        {idx > 0 && (
                            <span
                                style={{
                                    fontWeight: 700,
                                    backgroundColor: 'var(--rules-token-bg)',
                                    border: '1px solid var(--rules-token-border)',
                                    borderRadius: '8px',
                                    padding: '1px 6px',
                                    color: 'var(--rules-token-color)',
                                }}
                            >
                                {token}
                            </span>
                        )}
                        {part}
                    </span>
                ))}
            </>
        );
    };

    const exampleData = [
        {
            minimumPrice: 0,
            maximumPrice: 100,
            commission: 5.5,
            startDate: '01.01.2026',
            endDate: '31.01.2026',
        },
        {
            minimumPrice: 100,
            maximumPrice: 500,
            commission: 3.25,
            startDate: '01.01.2026',
            endDate: '31.01.2026',
        },
    ];

    const onUpload = async (file: File) => {
        setIsLoading(true);
        try {
            setUploadResponse(null);
            const formData = new FormData();
            formData.append('excel', file, file.name);
            formData.append('shippingCompanyId', selectedCompanyId);

            const response = await MPPaymentOnDeliveryCommissionCommand.InsertBulkCodCommissionRangesExcel(formData);
            if (response) {
                setUploadResponse(response);

                if (response.isSuccess === false) {
                    Swal.fire({
                        icon: 'error',
                        title: t('general.error'),
                        text: response.message || t('general.anErrorOccured'),
                        confirmButtonText: t('general.ok'),
                    });
                    return;
                }

                Swal.fire({
                    icon: 'success',
                    title: t('general.success'),
                    text:
                        t('mpPaymentOnDeliveryCommission.bulkUploadSuccess') +
                        ', ' +
                        t('mpPaymentOnDeliveryCommission.mismatchedDataWillBeShown'),
                    showConfirmButton: false,
                    timer: 2500,
                });
            }
        } catch {
            setUploadResponse(null);

            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: t('general.anErrorOccured'),
                confirmButtonText: t('general.ok'),
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleChangeCompany = (event: SelectChangeEvent) => {
        setSelectedCompanyId(event.target.value);
    };

    const renderStatusCounts = () => {
        if (!uploadResponse) return null;

        const messageText = String(uploadResponse.message || '');
        const insertedRecords = Number(messageText.match(/Yeni:\s*(\d+)/)?.[1] ?? 0);
        const updatedRecords = Number(messageText.match(/Güncellenen:\s*(\d+)/)?.[1] ?? 0);

        const failedRecords = Object.keys(uploadResponse.errors || {}).length;

        return (
            <Box className={MPODC.bulkTabStatusRow} sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginTop: 2, marginBottom: 2 }}>
                <Chip
                    label={`${t('mpPaymentOnDeliveryCommission.updated')}: ${updatedRecords}`}
                    sx={{ backgroundColor: '#ff9800', color: 'white' }}
                />
                <Chip
                    label={`${t('mpPaymentOnDeliveryCommission.inserted')}: ${insertedRecords}`}
                    sx={{ backgroundColor: '#4caf50', color: 'white' }}
                />
                {failedRecords > 0 && (
                    <Chip
                        label={`${t('mpPaymentOnDeliveryCommission.failed')}: ${failedRecords}`}
                        sx={{ backgroundColor: '#f44336', color: 'white' }}
                    />
                )}
            </Box>
        );
    };

    const renderFailedItemsTable = () => {
        if (!uploadResponse?.errors || Object.keys(uploadResponse.errors || {}).length === 0) return null;

        const failedRows = Object.entries(uploadResponse.errors || {}).map(([rowNumber, message]) => ({
            rowNumber,
            message,
            uniqueKey: rowNumber,
        }));

        return (
            <Box sx={{ marginTop: 3 }}>
                <p style={{ marginBottom: 2, color: '#f44336', fontWeight: 'bold', fontSize: '16px' }}>
                    {t('mpPaymentOnDeliveryCommission.failedRecords')}
                </p>
                <SCUICustomDataGrid
                    rows={failedRows}
                    getRowId={(row: any) => row.uniqueKey}
                    page={0}
                    pageSize={5}
                    columns={[
                        {
                            field: 'rowNumber',
                            headerName: t('mpPaymentOnDeliveryCommission.rowNumber'),
                            flex: 0.5,
                        },
                        {
                            field: 'message',
                            headerName: t('mpPaymentOnDeliveryCommission.message'),
                            flex: 2.5,
                        },
                    ]}
                />
            </Box>
        );
    };

    return (
        <Box className={MPODC.bulkTabRoot} width="100%">
            <p className={MPODC.bulkTabTitle} style={{ fontSize: '18px', fontWeight: 'bold' }}>
                {t('mpPaymentOnDeliveryCommission.bulkUpload')}
            </p>
            <p className={MPODC.bulkTabSubtitle} style={{ fontSize: '12px', marginTop: '8px' }}>
                {t('mpPaymentOnDeliveryCommission.bulkUploadText')}
            </p>

            <FormControl
                className={MPODC.bulkTabCompanyField}
                fullWidth
                sx={{ width: '40%', marginBottom: '16px', marginTop: '16px' }}
            >
                <InputLabel id="filter-company-label">
                    {t('mpPaymentOnDeliveryCommission.list.columns.cargoCompany')}
                </InputLabel>
                <Select
                    className={MPODC.inputSelectBulkTabCompany}
                    labelId="filter-company-label"
                    label={t('mpPaymentOnDeliveryCommission.list.columns.cargoCompany')}
                    value={selectedCompanyId}
                    onChange={handleChangeCompany}
                >
                    {(companyOptions || []).map(
                        (opt) =>
                            opt.id !== -1 && (
                                <MenuItem key={opt.id} value={opt.id}>
                                    {opt.name}
                                </MenuItem>
                            )
                    )}
                </Select>
            </FormControl>
            <Paper
                className={MPODC.bulkTabRulesPaper}
                sx={(theme) => ({
                    '--rules-title-color': '#1a1a1a',
                    '--rules-text-color': '#4a4a4a',
                    '--rules-token-bg': 'rgba(0,0,0,0.08)',
                    '--rules-token-border': 'rgba(0,0,0,0.15)',
                    '--rules-token-color': '#1a1a1a',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid rgba(0,0,0,0.12)',
                    ...theme.applyStyles('dark', {
                        '--rules-title-color': '#ffffff',
                        '--rules-text-color': '#b0b0b0',
                        '--rules-token-bg': 'rgba(255,255,255,0.1)',
                        '--rules-token-border': 'rgba(255,255,255,0.2)',
                        '--rules-token-color': '#e0e0e0',
                        backgroundColor: '#1e1e1e',
                        border: '1px solid rgba(255,255,255,0.12)',
                    }),
                    borderRadius: '8px',
                    padding: '14px 18px',
                    marginTop: '16px',
                    marginBottom: '16px',
                    boxShadow: 'none',
                })}
            >
                <p
                    style={{
                        margin: 0,
                        fontWeight: 'bold',
                        fontSize: '14px',
                        color: 'var(--rules-title-color)',
                    }}
                >
                    {t('mpPaymentOnDeliveryCommission.excelRules.title')}
                </p>
                <ul
                    style={{
                        margin: '10px 0 0 0',
                        paddingLeft: '18px',
                        fontSize: '12px',
                        lineHeight: '2',
                        color: 'var(--rules-text-color)',
                    }}
                >
                    <li>
                        {renderHighlightedToken(t('mpPaymentOnDeliveryCommission.excelRules.dateFormat'), 'dd.MM.yyyy')}
                    </li>
                    <li>
                        {renderHighlightedToken(
                            t('mpPaymentOnDeliveryCommission.excelRules.desiCannotBeNegative'),
                            t('mpPaymentOnDeliveryCommission.excelRules.negative')
                        )}
                    </li>
                    <li>
                        {renderHighlightedToken(
                            t('mpPaymentOnDeliveryCommission.excelRules.costCannotBeNegative'),
                            '2'
                        )}
                    </li>
                    <li>
                        {renderHighlightedToken(t('mpPaymentOnDeliveryCommission.excelRules.decimalSeparator'), '1,5')}
                    </li>
                    <li>{t('mpPaymentOnDeliveryCommission.excelRules.startBeforeEnd')}</li>
                    <li>{t('mpPaymentOnDeliveryCommission.excelRules.noOverlappingDateRanges')}</li>
                </ul>
            </Paper>
            <Box className={MPODC.bulkTabUploadWrap}>
                <SCUIGenericExcelUpload
                    onUpload={onUpload}
                    uploadButtonText={t('mpPaymentOnDeliveryCommission.upload')}
                    selectFileButtonText={t('mpPaymentOnDeliveryCommission.selectExcelFile')}
                    showExample={true}
                    exampleData={exampleData}
                    exampleButtonText={t('general.exampleExcel')}
                    loading={isLoading}
                    style={{ width: '100%' }}
                    disabled={!selectedCompanyId}
                    exampleFileName={t('mpPaymentOnDeliveryCommission.exampleFileName')}
                />
            </Box>

            {renderStatusCounts()}

            {renderFailedItemsTable()}
        </Box>
    );
};

export default BulkExcelUploadTab;
